import { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { MyReportsCard } from '@/features/reports';
import { MetricsCards } from './MetricsCards';
import {
  useDashboardMetrics,
  useRevenueChart,
  useJobStatusBreakdown,
  useStaffUtilizationSummary,
  useDashboardScopeContext,
  type RevenueDataPoint,
  type JobStatusBreakdown,
  type StaffUtilizationSummary,
} from './hooks/useDashboard';

/**
 * DashboardPage — firm-wide dashboard displaying key metrics, revenue chart,
 * job status breakdown, and staff utilization summary.
 * Requirements: 12.1, 12.6
 */
export function DashboardPage() {
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');

  // Role-driven filter context (teams + members the user may select, and
  // whether they get the manager two-dropdown UX). Scoped server-side.
  const { data: scopeContext } = useDashboardScopeContext();
  const role = scopeContext?.role ?? 'member';
  const currentUserId = scopeContext?.currentUserId;
  const teamOptions = useMemo(() => scopeContext?.teams ?? [], [scopeContext]);
  const allMembers = useMemo(() => scopeContext?.members ?? [], [scopeContext]);

  // A member (accountant/partner) can never change the member selection — it is
  // pinned to themselves. Only managers/admins may pick a different member.
  const memberDropdownLocked = role === 'member';

  // The team dropdown is changeable only when the user belongs to (or can pick)
  // more than one team; a single team is shown but locked.
  const isSingleTeam = teamOptions.length === 1;

  useEffect(() => {
    if (isSingleTeam && selectedTeamId !== teamOptions[0].id) {
      setSelectedTeamId(teamOptions[0].id);
    }
  }, [isSingleTeam, teamOptions, selectedTeamId]);

  // Member options: filtered to the selected team (or all selectable members
  // when "All Teams" is chosen). For a member the list is just themselves.
  const memberOptions = useMemo(() => {
    if (!selectedTeamId) return allMembers;
    return allMembers.filter((m) => m.teamIds.includes(selectedTeamId));
  }, [selectedTeamId, allMembers]);

  // Members are always pinned to their own id; clear a stale manager/admin
  // selection that no longer belongs to the visible set.
  useEffect(() => {
    if (memberDropdownLocked) {
      if (currentUserId && selectedMemberId !== currentUserId) {
        setSelectedMemberId(currentUserId);
      }
      return;
    }
    if (selectedMemberId && !memberOptions.some((m) => m.id === selectedMemberId)) {
      setSelectedMemberId('');
    }
  }, [memberDropdownLocked, currentUserId, memberOptions, selectedMemberId]);

  const teamId = selectedTeamId || undefined;
  const memberId = selectedMemberId || undefined;

  // Each dashboard card is gated by its own dedicated dashboard permission, so
  // components can be shown/hidden per role independently of module access.
  // Administrators bypass and see every component. We also skip fetching data
  // for cards the user can't see.
  const { can, isAdmin } = usePermissions();
  const canView = (permission: string) => isAdmin || can(permission);

  const canSeeMetrics = canView(PERMISSIONS.DASHBOARD_VIEW_METRICS);
  const canSeeRevenue = canView(PERMISSIONS.DASHBOARD_VIEW_REVENUE);
  const canSeeJobStatus = canView(PERMISSIONS.DASHBOARD_VIEW_JOB_STATUS);
  const canSeeStaffUtilization = canView(PERMISSIONS.DASHBOARD_VIEW_STAFF_UTILIZATION);
  const canSeeMyReports = canView(PERMISSIONS.DASHBOARD_VIEW_MY_REPORTS);
  const canExportReports = can(PERMISSIONS.REPORTING_EXPORT);

  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics(teamId, memberId, {
    enabled: canSeeMetrics,
  });
  const { data: revenueData, isLoading: revenueLoading } = useRevenueChart(teamId, memberId, {
    enabled: canSeeRevenue,
  });
  const { data: jobStatus, isLoading: jobStatusLoading } = useJobStatusBreakdown(teamId, memberId, {
    enabled: canSeeJobStatus,
  });
  const { data: staffUtilization, isLoading: staffLoading } = useStaffUtilizationSummary(
    teamId,
    memberId,
    { enabled: canSeeStaffUtilization },
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {selectedMemberId
              ? 'Member performance overview'
              : selectedTeamId
                ? 'Team performance overview'
                : role === 'member'
                  ? 'Your performance overview'
                  : 'Firm performance overview'}{' '}
            — data refreshes every 5 minutes
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Team dropdown — shown to everyone who has selectable teams */}
          {teamOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <label htmlFor="dashboard-team" className="text-xs text-gray-500 dark:text-gray-400">
                Team:
              </label>
              <select
                id="dashboard-team"
                value={selectedTeamId}
                onChange={(e) => {
                  setSelectedTeamId(e.target.value);
                  setSelectedMemberId('');
                }}
                disabled={isSingleTeam}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-70 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {!isSingleTeam && <option value="">All Teams</option>}
                {teamOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Member dropdown. Managers/admins can pick any team member (with an
              "All Members" option); a member (accountant/partner) sees only
              their own name and the control is disabled. */}
          {memberOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="dashboard-member"
                className="text-xs text-gray-500 dark:text-gray-400"
              >
                Member:
              </label>
              <select
                id="dashboard-member"
                value={selectedMemberId}
                onChange={(e) => setSelectedMemberId(e.target.value)}
                disabled={memberDropdownLocked}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-70 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {!memberDropdownLocked && <option value="">All Members</option>}
                {memberOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Metrics Cards */}
      {canSeeMetrics && <MetricsCards metrics={metrics} isLoading={metricsLoading} />}

      {/* Charts Row — only rendered when at least one chart is permitted, so
          we never show an empty grid row. */}
      {(canSeeRevenue || canSeeJobStatus) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Revenue Overview */}
          {canSeeRevenue && (
            <Card>
              <CardHeader
                title="Revenue Overview"
                description="Revenue vs collected amounts by period"
              />
              <CardContent>
                <RevenueBarChart data={revenueData} isLoading={revenueLoading} />
              </CardContent>
            </Card>
          )}

          {/* Job Status Breakdown */}
          {canSeeJobStatus && (
            <Card>
              <CardHeader
                title="Job Status Breakdown"
                description="Current distribution of jobs by status"
              />
              <CardContent>
                <JobStatusChart data={jobStatus} isLoading={jobStatusLoading} />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Staff Utilization */}
      {canSeeStaffUtilization && (
        <Card>
          <CardHeader
            title="Staff Utilization"
            description="Team member workload and capacity overview"
          />
          <CardContent>
            <StaffUtilizationTable data={staffUtilization} isLoading={staffLoading} />
          </CardContent>
        </Card>
      )}

      {/* My Reports — the user's pinned reports, run in place */}
      {canSeeMyReports && <MyReportsCard canExport={canExportReports} />}
    </div>
  );
}

// ─── Revenue Bar Chart (CSS-based) ─────────────────────────────────────────

interface RevenueBarChartProps {
  data: RevenueDataPoint[] | undefined;
  isLoading: boolean;
}

function RevenueBarChart({ data, isLoading }: RevenueBarChartProps) {
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">No revenue data available</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => Math.max(d.revenue, d.collected)), 1);

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex items-center gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-brand-500" aria-hidden="true" />
          <span className="text-gray-600 dark:text-gray-400">Revenue</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-green-500" aria-hidden="true" />
          <span className="text-gray-600 dark:text-gray-400">Collected</span>
        </div>
      </div>

      {/* Bar Chart */}
      <div
        className="space-y-3"
        role="img"
        aria-label="Revenue chart showing revenue and collected amounts by period"
      >
        {data.map((point) => (
          <div key={point.period} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-gray-700 dark:text-gray-300">{point.period}</span>
              <span className="text-gray-500 dark:text-gray-400">
                ${point.revenue.toLocaleString('en-CA', { minimumFractionDigits: 0 })}
              </span>
            </div>
            <div className="space-y-1">
              <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-full rounded-full bg-brand-500 transition-all duration-500"
                  style={{ width: `${(point.revenue / maxValue) * 100}%` }}
                />
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-full rounded-full bg-green-500 transition-all duration-500"
                  style={{ width: `${(point.collected / maxValue) * 100}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Job Status Chart (CSS-based donut) ─────────────────────────────────────

interface JobStatusChartProps {
  data: JobStatusBreakdown[] | undefined;
  isLoading: boolean;
}

function JobStatusChart({ data, isLoading }: JobStatusChartProps) {
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">No job data available</p>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row">
      {/* Donut visualization */}
      <div className="relative flex h-40 w-40 items-center justify-center">
        <svg viewBox="0 0 36 36" className="h-40 w-40" aria-hidden="true">
          {renderDonutSegments(data, total)}
        </svg>
        <div className="absolute text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{total}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
        </div>
      </div>

      {/* Legend */}
      <div className="flex-1 space-y-2" role="list" aria-label="Job status breakdown">
        {data.map((item) => (
          <div key={item.status} className="flex items-center justify-between" role="listitem">
            <div className="flex items-center gap-2">
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: item.color }}
                aria-hidden="true"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {item.count}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({total > 0 ? Math.round((item.count / total) * 100) : 0}%)
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Render SVG donut segments for job status breakdown. */
function renderDonutSegments(data: JobStatusBreakdown[], total: number) {
  if (total === 0) return null;

  const segments: React.ReactNode[] = [];
  let cumulativePercent = 0;

  data.forEach((item, index) => {
    const percent = (item.count / total) * 100;
    const dashArray = `${percent} ${100 - percent}`;
    const dashOffset = 100 - cumulativePercent + 25; // +25 to start from top

    segments.push(
      <circle
        key={index}
        cx="18"
        cy="18"
        r="15.9155"
        fill="none"
        stroke={item.color}
        strokeWidth="3"
        strokeDasharray={dashArray}
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
      />,
    );

    cumulativePercent += percent;
  });

  return segments;
}

// ─── Staff Utilization Table ────────────────────────────────────────────────

interface StaffUtilizationTableProps {
  data: StaffUtilizationSummary[] | undefined;
  isLoading: boolean;
}

function StaffUtilizationTable({ data, isLoading }: StaffUtilizationTableProps) {
  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        No staff utilization data available
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm" role="grid">
        <thead>
          <tr className="border-b border-border text-gray-600 dark:border-border-dark dark:text-gray-400">
            <th className="px-4 py-3 font-medium">Staff Member</th>
            <th className="px-4 py-3 font-medium text-center">Active Jobs</th>
            <th className="px-4 py-3 font-medium">Utilization</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border dark:divide-border-dark">
          {data.map((staff) => (
            <tr
              key={staff.userId}
              className="transition-colors hover:bg-surface-secondary dark:hover:bg-surface-dark-tertiary"
            >
              <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                {staff.userName}
              </td>
              <td className="px-4 py-3 text-center">
                <Badge variant="info" size="sm">
                  {staff.activeJobs}
                </Badge>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-24 rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className={`h-2 rounded-full transition-all ${getUtilizationColor(staff.utilizationPercentage)}`}
                      style={{ width: `${Math.min(staff.utilizationPercentage, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {staff.utilizationPercentage}%
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getUtilizationColor(percentage: number): string {
  if (percentage >= 100) return 'bg-red-500';
  if (percentage >= 80) return 'bg-yellow-500';
  if (percentage >= 50) return 'bg-brand-500';
  return 'bg-green-500';
}

function LoadingSpinner() {
  return (
    <div className="flex items-center gap-2 text-gray-500" aria-live="polite">
      <svg
        className="h-5 w-5 animate-spin text-brand-500"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      Loading...
    </div>
  );
}
